/**
 * Centralized Permission Definitions for Forge RBAC
 * 
 * This file defines the permission model for the application:
 * - Modules: Core feature areas of the app
 * - Actions: Operations that can be performed within modules
 * - Role Permissions: Mapping of app roles to their allowed actions per module
 */

import type { AppRole } from "@/hooks/useUserRoles";

// ============================================================================
// Module Definitions
// ============================================================================

export const MODULES = [
  'schools',
  'childcare',
  'directory',
  'hr',
  'people',
  'households',
  'contributions',
  'funds',
  'pledges',
  'requests',
  'projects',
  'tasks',
  'services',
  'attendance',
  'calendar',
  'assets',
  'messaging',
  'announcements',
  'integrations',
  'reports',
  'settings',
] as const;

export type Module = typeof MODULES[number];

// ============================================================================
// Action Definitions
// ============================================================================

export const ACTIONS = ['view', 'add', 'edit', 'delete', 'export', 'manage'] as const;
export type Action = typeof ACTIONS[number];

// ============================================================================
// Permission Set Type
// ============================================================================

export type PermissionSet = {
  [K in Action]?: boolean;
};

export type ModulePermissions = {
  [K in Module]?: PermissionSet;
};

// ============================================================================
// Role Permission Mappings
// ============================================================================

const FULL_ACCESS: PermissionSet = {
  view: true,
  add: true,
  edit: true,
  delete: true,
  export: true,
  manage: true,
};

const READ_ONLY: PermissionSet = {
  view: true,
  export: true,
};

const OPERATIONAL: PermissionSet = {
  view: true,
  add: true,
  edit: true,
  export: true,
};

// FULL_ACCESS minus delete. platform_support needs this for contributions/
// funds/pledges: the server never authorized platform_support to delete
// financial transactions (set_contributions_deleted_at, pledges_update_authenticated,
// funds_delete_authenticated all omit it), so granting it here just showed a
// delete affordance in the UI that always 42501s. Support-initiated deletes go
// through an admin/finance user via impersonation instead — see #6393.
const FULL_ACCESS_NO_DELETE: PermissionSet = {
  view: true,
  add: true,
  edit: true,
  export: true,
  manage: true,
};

// platform_support on contributions: read and export, plus the `manage` bit, but not add or edit.
// can_write_contributions_org (and the contributions insert/update policies) never admit
// platform_support: "Platform support remains read-only". So add/edit here offered the record-gift
// controls (quick add, the batch entry page, event "Paid", the person's Giving tab) and a gift's
// Edit/review actions, which then failed with 42501 (#7838). `manage` is deliberately left as it
// was: it also gates finance affordances that have nothing to do with recording a gift
// (useFinancePermissions' canApproveBills and canRunAllocations, the on-behalf request flow), and
// those are decided with the finance capability matrix (#8123, #6668), not here.
const CONTRIBUTIONS_SUPPORT_READ: PermissionSet = {
  view: true,
  export: true,
  manage: true,
};

/**
 * Role Permission Matrix
 * 
 * Each role is mapped to its allowed permissions per module.
 * Permissions are additive when a user has multiple roles.
 */
export const ROLE_PERMISSIONS: Record<AppRole, ModulePermissions> = {
  // Super Admin: Platform-level access (handled separately via super_admin checks)
  super_admin: {
    childcare: FULL_ACCESS,
    directory: FULL_ACCESS,
    hr: FULL_ACCESS,
    people: FULL_ACCESS,
    households: FULL_ACCESS,
    contributions: FULL_ACCESS,
    funds: FULL_ACCESS,
    pledges: FULL_ACCESS,
    requests: FULL_ACCESS,
    projects: FULL_ACCESS,
    tasks: FULL_ACCESS,
    services: FULL_ACCESS,
    attendance: FULL_ACCESS,
    calendar: FULL_ACCESS,
    assets: FULL_ACCESS,
    messaging: FULL_ACCESS,
    announcements: FULL_ACCESS,
    integrations: FULL_ACCESS,
    reports: FULL_ACCESS,
    settings: FULL_ACCESS,
  },
  platform_super_admin: {
    childcare: FULL_ACCESS,
    directory: FULL_ACCESS,
    hr: FULL_ACCESS,
    people: FULL_ACCESS,
    households: FULL_ACCESS,
    contributions: FULL_ACCESS,
    funds: FULL_ACCESS,
    pledges: FULL_ACCESS,
    requests: FULL_ACCESS,
    projects: FULL_ACCESS,
    tasks: FULL_ACCESS,
    services: FULL_ACCESS,
    attendance: FULL_ACCESS,
    calendar: FULL_ACCESS,
    assets: FULL_ACCESS,
    messaging: FULL_ACCESS,
    announcements: FULL_ACCESS,
    integrations: FULL_ACCESS,
    reports: FULL_ACCESS,
    settings: FULL_ACCESS,
  },
  platform_support: {
    childcare: FULL_ACCESS,
    directory: FULL_ACCESS,
    hr: FULL_ACCESS,
    people: FULL_ACCESS,
    households: FULL_ACCESS,
    contributions: CONTRIBUTIONS_SUPPORT_READ,
    funds: FULL_ACCESS_NO_DELETE,
    pledges: FULL_ACCESS_NO_DELETE,
    requests: FULL_ACCESS,
    projects: FULL_ACCESS,
    tasks: FULL_ACCESS,
    services: FULL_ACCESS,
    attendance: FULL_ACCESS,
    calendar: FULL_ACCESS,
    assets: FULL_ACCESS,
    messaging: FULL_ACCESS,
    announcements: FULL_ACCESS,
    integrations: FULL_ACCESS,
    reports: FULL_ACCESS,
    settings: FULL_ACCESS,
  },
  platform_readonly: {
    childcare: READ_ONLY,
    directory: READ_ONLY,
    hr: READ_ONLY,
    people: READ_ONLY,
    households: READ_ONLY,
    contributions: READ_ONLY,
    funds: READ_ONLY,
    pledges: READ_ONLY,
    requests: READ_ONLY,
    projects: READ_ONLY,
    tasks: READ_ONLY,
    services: READ_ONLY,
    attendance: READ_ONLY,
    calendar: READ_ONLY,
    assets: READ_ONLY,
    messaging: READ_ONLY,
    announcements: READ_ONLY,
    integrations: READ_ONLY,
    reports: READ_ONLY,
    settings: READ_ONLY,
  },

  // Admin: Full organizational access
  admin: {
    childcare: FULL_ACCESS,
    directory: FULL_ACCESS,
    hr: FULL_ACCESS,
    people: FULL_ACCESS,
    households: FULL_ACCESS,
    contributions: FULL_ACCESS,
    funds: FULL_ACCESS,
    pledges: FULL_ACCESS,
    requests: FULL_ACCESS,
    projects: FULL_ACCESS,
    tasks: FULL_ACCESS,
    services: FULL_ACCESS,
    attendance: FULL_ACCESS,
    calendar: FULL_ACCESS,
    assets: FULL_ACCESS,
    messaging: FULL_ACCESS,
    announcements: FULL_ACCESS,
    integrations: FULL_ACCESS,
    reports: FULL_ACCESS,
    settings: FULL_ACCESS,
  },

  // Finance: Financial operations focus
  finance: {
    childcare: {},
    directory: READ_ONLY,
    hr: READ_ONLY,
    people: READ_ONLY,
    households: READ_ONLY,
    contributions: FULL_ACCESS,
    funds: FULL_ACCESS,
    pledges: FULL_ACCESS,
    requests: { view: true }, // Can see requests but not manage
    projects: READ_ONLY,
    tasks: READ_ONLY,
    services: READ_ONLY,
    attendance: READ_ONLY,
    calendar: READ_ONLY,
    assets: READ_ONLY,
    messaging: { view: true, add: true }, // Can send messages
    announcements: READ_ONLY,
    integrations: { view: true, manage: true }, // Can manage finance integrations
    reports: FULL_ACCESS,
    settings: READ_ONLY,
  },

  // Treasurer: Financial oversight with restriction release and period close capabilities
  treasurer: {
    childcare: {},
    directory: READ_ONLY,
    hr: READ_ONLY,
    people: READ_ONLY,
    households: READ_ONLY,
    contributions: FULL_ACCESS,  // Full access including releases
    funds: READ_ONLY,            // O5: funds write RLS = admin|finance only (migration 20260615220000); treasurer excluded — view/export only to avoid dead-end buttons (RLS-broadening for finance leaders is a separate finance-owner decision)
    pledges: OPERATIONAL,
    requests: { view: true },
    projects: READ_ONLY,
    tasks: READ_ONLY,
    services: READ_ONLY,
    attendance: READ_ONLY,
    calendar: READ_ONLY,
    assets: READ_ONLY,
    messaging: { view: true, add: true },
    announcements: READ_ONLY,
    integrations: { view: true },
    reports: FULL_ACCESS,        // Full report access
    settings: { view: true, edit: true, manage: true }, // Can manage finance settings including period close
  },

  // Bookkeeper: Day-to-day transaction entry without restriction release or period close
  bookkeeper: {
    childcare: {},
    directory: READ_ONLY,
    hr: {},
    people: READ_ONLY,
    households: READ_ONLY,
    contributions: OPERATIONAL,  // Can enter/edit but not delete or manage
    funds: READ_ONLY,            // O5: funds write RLS = admin|finance only (migration 20260615220000); bookkeeper add/edit dead-ends — view/export only
    pledges: OPERATIONAL,
    requests: { view: true },
    projects: READ_ONLY,
    tasks: READ_ONLY,
    services: READ_ONLY,
    attendance: READ_ONLY,
    calendar: READ_ONLY,
    assets: READ_ONLY,
    messaging: { view: true },
    announcements: READ_ONLY,
    integrations: { view: true },
    reports: { view: true, export: true },  // Read-only reports
    settings: READ_ONLY,                    // Cannot change settings or close periods
  },

  // Pastor/Care Lead: People and pastoral care focus
  pastor: {
    childcare: OPERATIONAL,
    directory: OPERATIONAL,
    hr: READ_ONLY,
    people: FULL_ACCESS,
    households: FULL_ACCESS,
    contributions: { view: true }, // Limited financial visibility
    funds: READ_ONLY,
    pledges: READ_ONLY,
    requests: FULL_ACCESS, // Full access to care requests
    projects: OPERATIONAL,
    tasks: OPERATIONAL,
    services: FULL_ACCESS, // Full access to service planning
    attendance: OPERATIONAL,
    calendar: OPERATIONAL,
    assets: READ_ONLY,
    messaging: FULL_ACCESS,
    announcements: OPERATIONAL,
    integrations: READ_ONLY,
    reports: { view: true, export: true },
    settings: READ_ONLY,
  },

  // Youth: Engagement-focused access for youth ministry
  youth: {
    childcare: OPERATIONAL,
    directory: OPERATIONAL,
    hr: {}, // L8: no HR access — additive perms were unlocking the HR nav for youth personas
    people: READ_ONLY, // O2: people write RLS excludes youth; keep view, drop add/edit
    households: READ_ONLY,
    contributions: READ_ONLY,
    funds: READ_ONLY,
    pledges: READ_ONLY,
    requests: OPERATIONAL,
    projects: OPERATIONAL,
    tasks: OPERATIONAL,
    services: OPERATIONAL,
    attendance: OPERATIONAL,
    calendar: OPERATIONAL,
    assets: READ_ONLY,
    messaging: OPERATIONAL,
    announcements: OPERATIONAL,
    integrations: READ_ONLY,
    reports: READ_ONLY,
    settings: READ_ONLY,
  },

  // Volunteer Coordinator: Scheduling and volunteer oversight
  volunteer_coordinator: {
    childcare: OPERATIONAL,
    directory: OPERATIONAL,
    hr: READ_ONLY,
    people: READ_ONLY, // O2: people write RLS excludes volunteer_coordinator; keep view, drop add/edit
    households: READ_ONLY,
    contributions: READ_ONLY,
    funds: READ_ONLY,
    pledges: READ_ONLY,
    requests: OPERATIONAL,
    projects: OPERATIONAL,
    tasks: OPERATIONAL,
    services: OPERATIONAL,
    attendance: OPERATIONAL,
    calendar: OPERATIONAL,
    assets: READ_ONLY,
    messaging: OPERATIONAL,
    announcements: OPERATIONAL,
    integrations: READ_ONLY,
    reports: READ_ONLY,
    settings: READ_ONLY,
  },

  // Facilities: Work order and asset focus
  facilities: {
    childcare: READ_ONLY,
    directory: READ_ONLY,
    hr: READ_ONLY,
    people: READ_ONLY,
    households: READ_ONLY,
    contributions: READ_ONLY,
    funds: READ_ONLY,
    pledges: READ_ONLY,
    requests: OPERATIONAL,
    projects: OPERATIONAL,
    tasks: OPERATIONAL,
    services: READ_ONLY,
    attendance: READ_ONLY,
    calendar: OPERATIONAL,
    assets: FULL_ACCESS,
    messaging: OPERATIONAL,
    announcements: OPERATIONAL,
    integrations: READ_ONLY,
    reports: READ_ONLY,
    settings: READ_ONLY,
  },

  // Staff: Broad operational access
  staff: {
    childcare: OPERATIONAL,
    directory: OPERATIONAL,
    hr: READ_ONLY,
    people: OPERATIONAL,
    households: OPERATIONAL,
    contributions: {}, // No access per staff role expectations
    funds: READ_ONLY,
    pledges: READ_ONLY,
    requests: OPERATIONAL,
    projects: OPERATIONAL,
    tasks: OPERATIONAL,
    services: OPERATIONAL,
    attendance: OPERATIONAL,
    calendar: OPERATIONAL,
    assets: OPERATIONAL,
    messaging: { view: true, add: true },
    announcements: OPERATIONAL,
    integrations: READ_ONLY,
    reports: READ_ONLY,
    settings: READ_ONLY,
  },

  // Ministry Leader: Operational access focused on ministry workflows (no HR)
  ministry_leader: {
    childcare: OPERATIONAL,
    directory: OPERATIONAL,
    hr: {}, // No HR access — ministry leaders view ministry-scoped data only
    people: READ_ONLY, // O2: people write RLS excludes ministry_leader; keep view, drop add/edit
    households: OPERATIONAL,
    contributions: { view: true }, // O5: contributions INSERT RLS excludes ministry_leader; view-only
    funds: READ_ONLY,
    pledges: READ_ONLY,
    requests: OPERATIONAL,
    projects: OPERATIONAL,
    tasks: OPERATIONAL,
    services: OPERATIONAL,
    attendance: OPERATIONAL,
    calendar: OPERATIONAL,
    assets: OPERATIONAL,
    messaging: { view: true, add: true },
    announcements: OPERATIONAL,
    integrations: READ_ONLY,
    reports: READ_ONLY,
    settings: READ_ONLY,
  },

  // Volunteer: Minimal, read-focused access
  volunteer: {
    childcare: { view: true, add: true },
    directory: { view: true },
    hr: {},
    people: { view: true },
    households: { view: true },
    contributions: {}, // No access
    funds: {}, // No access
    pledges: {}, // No access
    requests: { view: true }, // Can see assigned requests only (enforced via RLS)
    projects: { view: true },
    tasks: { view: true, edit: true }, // Can update assigned tasks
    services: { view: true }, // Can view services they're assigned to
    attendance: { view: true },
    calendar: { view: true },
    assets: { view: true },
    messaging: {},
    announcements: {},
    integrations: {},
    reports: {},
    settings: {},
  },

  // Member: Very minimal access for self-service portal users
  member: {
    childcare: {},
    directory: { view: true, edit: true }, // View directory and manage own profile
    hr: {},
    people: { view: true }, // Member directory access
    households: {},
    contributions: {},
    funds: {},
    pledges: {},
    requests: { view: true, add: true }, // Can submit and view own requests
    projects: {},
    tasks: {},
    services: {},
    attendance: { view: true }, // Can view own attendance
    calendar: { view: true }, // Can view public events
    assets: {},
    messaging: {},
    announcements: {},
    integrations: {},
    reports: {},
    settings: {},
  },

  // Auditor: Read-only across everything
  auditor: {
    childcare: READ_ONLY,
    directory: READ_ONLY,
    hr: READ_ONLY,
    people: READ_ONLY,
    households: READ_ONLY,
    contributions: READ_ONLY,
    funds: READ_ONLY,
    pledges: READ_ONLY,
    requests: READ_ONLY,
    projects: READ_ONLY,
    tasks: READ_ONLY,
    services: READ_ONLY,
    attendance: READ_ONLY,
    calendar: READ_ONLY,
    assets: READ_ONLY,
    messaging: READ_ONLY,
    announcements: READ_ONLY,
    integrations: READ_ONLY,
    reports: READ_ONLY,
    settings: READ_ONLY,
  },

  // Finance Admin: Full finance authority (server RLS gates the accounting subsystem)
  finance_admin: {
    childcare: {},
    directory: READ_ONLY,
    hr: READ_ONLY,
    people: READ_ONLY,
    households: READ_ONLY,
    contributions: FULL_ACCESS,
    funds: READ_ONLY,            // O5: funds write RLS = admin|finance only (migration 20260615220000); finance_admin's authority is the accounting subsystem (server-enforced), not church-giving funds writes — view/export only
    pledges: FULL_ACCESS,
    requests: { view: true },
    projects: READ_ONLY,
    tasks: READ_ONLY,
    services: READ_ONLY,
    attendance: READ_ONLY,
    calendar: READ_ONLY,
    assets: READ_ONLY,
    messaging: { view: true, add: true },
    announcements: READ_ONLY,
    integrations: { view: true, manage: true },
    reports: FULL_ACCESS,
    settings: { view: true, edit: true, manage: true },
  },

  // AP Clerk: Accounts-payable transaction entry. Client opens the finance UI
  // (gated by the `contributions` module); the AP lane itself is enforced
  // server-side (finance_ap_* RLS already lists ap_clerk).
  ap_clerk: {
    childcare: {},
    directory: {}, // O7: people SELECT RLS excludes ap_clerk → self-only directory; hide People nav
    hr: {},
    people: {}, // O7: people SELECT RLS excludes ap_clerk; finance UI reached via contributions module
    households: READ_ONLY,
    // View-only on church-giving contributions — just enough to open the finance
    // UI (gated by module="contributions"). AP entry is the finance_ap_* tables,
    // enforced server-side.
    contributions: { view: true },
    funds: { view: true },
    pledges: {},
    requests: { view: true },
    projects: {},
    tasks: READ_ONLY,
    services: {},
    attendance: {},
    calendar: READ_ONLY,
    assets: READ_ONLY,
    messaging: { view: true },
    announcements: READ_ONLY,
    integrations: { view: true },
    reports: { view: true, export: true },
    settings: READ_ONLY,
  },

  // AR Clerk: Accounts-receivable transaction entry. Same client surface as
  // ap_clerk; the AR lane is enforced server-side (finance_ar_* RLS lists ar_clerk).
  ar_clerk: {
    childcare: {},
    directory: {}, // O7: people SELECT RLS excludes ar_clerk → self-only directory; hide People nav
    hr: {},
    people: {}, // O7: people SELECT RLS excludes ar_clerk; finance UI reached via contributions module
    households: READ_ONLY,
    // View-only on church-giving contributions (see ap_clerk). AR entry is the
    // finance_ar_* tables, enforced server-side.
    contributions: { view: true },
    funds: { view: true },
    pledges: {},
    requests: { view: true },
    projects: {},
    tasks: READ_ONLY,
    services: {},
    attendance: {},
    calendar: READ_ONLY,
    assets: READ_ONLY,
    messaging: { view: true },
    announcements: READ_ONLY,
    integrations: { view: true },
    reports: { view: true, export: true },
    settings: READ_ONLY,
  },

  // Approver: Approval gate. Reads finance and can act on approvals (edit);
  // cannot create/delete. Approval authority is enforced server-side.
  approver: {
    childcare: {},
    directory: {}, // O7: people SELECT RLS excludes approver → self-only directory; hide People nav
    hr: {},
    people: {}, // O7: people SELECT RLS excludes approver; finance UI reached via contributions module
    households: READ_ONLY,
    // View-only on church-giving contributions — approver acts on the finance
    // approval subsystem (finance_approval_*), not church-giving records.
    contributions: { view: true },
    funds: READ_ONLY,
    pledges: READ_ONLY,
    requests: { view: true },
    projects: {},
    tasks: READ_ONLY,
    services: {},
    attendance: {},
    calendar: READ_ONLY,
    assets: READ_ONLY,
    messaging: { view: true },
    announcements: READ_ONLY,
    integrations: { view: true },
    reports: { view: true, export: true },
    settings: READ_ONLY,
  },

};

for (const role of Object.keys(ROLE_PERMISSIONS) as AppRole[]) {
  ROLE_PERMISSIONS[role].schools ??= ROLE_PERMISSIONS[role].childcare;
}

// ============================================================================
// Role Display Information
// ============================================================================

export interface RoleInfo {
  label: string;
  description: string;
  summary: string;
}

export const ROLE_INFO: Record<AppRole, RoleInfo> = {
  super_admin: {
    label: 'Super Admin',
    description: 'Platform administrator with full system access',
    summary: 'Full platform access for platform administrators',
  },
  platform_super_admin: {
    label: 'Platform Super Admin',
    description: 'Full platform access and administrative controls',
    summary: 'Full platform access for platform administrators',
  },
  platform_support: {
    label: 'Platform Support',
    description: 'Support access for troubleshooting and customer care',
    summary: 'Support-level access for platform operations',
  },
  platform_readonly: {
    label: 'Platform Read-only',
    description: 'Read-only visibility across platform data',
    summary: 'Read-only access for platform auditing',
  },
  admin: {
    label: 'Admin',
    description: 'Organization administrator with full access to all modules',
    summary: 'Full access to all features and settings',
  },
  finance: {
    label: 'Finance',
    description: 'Manages contributions, funds, pledges, and financial reporting',
    summary: 'Contributions, funds, pledges, and financial reports',
  },
  treasurer: {
    label: 'Treasurer',
    description: 'Financial oversight — can release restrictions, close periods, approve bills, and run allocations',
    summary: 'Financial oversight with restriction release and period management',
  },
  bookkeeper: {
    label: 'Bookkeeper',
    description: 'Day-to-day transactions — can enter and categorize transactions, run reports, but cannot release restrictions or close periods',
    summary: 'Daily transaction entry and reporting without period management',
  },
  finance_admin: {
    label: 'Finance Admin',
    description: 'Full finance authority across the accounting subsystem — AP, AR, GL, budgets, and period close',
    summary: 'Full finance and accounting administration',
  },
  ap_clerk: {
    label: 'AP Clerk',
    description: 'Accounts payable — enters and processes vendor bills and payments',
    summary: 'Accounts-payable transaction entry',
  },
  ar_clerk: {
    label: 'AR Clerk',
    description: 'Accounts receivable — enters and processes customer invoices and receipts',
    summary: 'Accounts-receivable transaction entry',
  },
  approver: {
    label: 'Approver',
    description: 'Reviews and approves finance transactions (bills, payments, releases); cannot create or delete',
    summary: 'Finance approval authority',
  },
  pastor: {
    label: 'Pastor',
    description: 'Manages people, pastoral care requests, and messaging',
    summary: 'People, care requests, messaging, and attendance',
  },
  youth: {
    label: 'Youth',
    description: 'Leads youth ministry engagement, events, and care follow-ups',
    summary: 'Youth engagement, events, and volunteer coordination',
  },
  volunteer_coordinator: {
    label: 'Volunteer Coordinator',
    description: 'Coordinates volunteer scheduling, communications, and follow-ups',
    summary: 'Volunteer scheduling, messaging, and service oversight',
  },
  facilities: {
    label: 'Facilities',
    description: 'Manages facilities requests, assets, and maintenance operations',
    summary: 'Facilities requests, assets, and work orders',
  },
  staff: {
    label: 'Staff',
    description: 'Operational access to most modules with some restrictions',
    summary: 'Day-to-day operations across most modules',
  },
  ministry_leader: {
    label: 'Ministry Leader',
    description: 'Leads ministry operations, communications, and scheduling',
    summary: 'Ministry operations, announcements, and team coordination',
  },
  volunteer: {
    label: 'Volunteer',
    description: 'Limited access focused on assigned tasks and basic visibility',
    summary: 'View people, update assigned tasks',
  },
  auditor: {
    label: 'Auditor',
    description: 'Read-only access across all modules for compliance review',
    summary: 'View and export all data (read-only)',
  },
  member: {
    label: 'Member',
    description: 'Self-service portal access with limited visibility',
    summary: 'View own data and submit requests',
  },
};

// ============================================================================
// Module Display Information
// ============================================================================

export interface ModuleInfo {
  label: string;
  description: string;
  icon: string; // Lucide icon name
}

export const MODULE_INFO: Record<Module, ModuleInfo> = {
  schools: { label: 'Schools', description: 'School operations, academics, billing, and family workflows', icon: 'GraduationCap' },
  childcare: { label: 'Childcare', description: 'Kids check-in and pickup workflows', icon: 'Baby' },
  directory: { label: 'Directory', description: 'Member directory and contact sharing', icon: 'BookUser' },
  hr: { label: 'HR', description: 'Staff records, onboarding, and time off', icon: 'ShieldCheck' },
  people: { label: 'People', description: 'Manage congregation members and contacts', icon: 'Users' },
  households: { label: 'Households', description: 'Group people into household units', icon: 'Home' },
  contributions: { label: 'Contributions', description: 'Record and manage donations', icon: 'HandCoins' },
  funds: { label: 'Funds', description: 'Configure giving funds and categories', icon: 'Wallet' },
  pledges: { label: 'Pledges', description: 'Track giving commitments', icon: 'Target' },
  requests: { label: 'Requests', description: 'Handle care and facility requests', icon: 'MessageSquare' },
  projects: { label: 'Projects', description: 'Manage events and campaigns', icon: 'FolderKanban' },
  tasks: { label: 'Tasks', description: 'Track work items and assignments', icon: 'CheckSquare' },
  services: { label: 'Services', description: 'Plan worship services and volunteer scheduling', icon: 'Church' },
  attendance: { label: 'Attendance', description: 'Record event attendance', icon: 'CalendarCheck' },
  calendar: { label: 'Calendar', description: 'Schedule events and activities', icon: 'Calendar' },
  assets: { label: 'Assets', description: 'Track equipment and facilities', icon: 'Package' },
  messaging: { label: 'Messaging', description: 'Send emails and SMS', icon: 'Send' },
  announcements: { label: 'Announcements', description: 'Publish internal announcements', icon: 'Megaphone' },
  integrations: { label: 'Integrations', description: 'Connect external services', icon: 'Plug' },
  reports: { label: 'Reports', description: 'Generate and export reports', icon: 'FileText' },
  settings: { label: 'Settings', description: 'Configure organization settings', icon: 'Settings' },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get merged permissions for a set of roles.
 * Permissions are additive - if any role grants a permission, it's allowed.
 */
export function getMergedPermissions(roles: AppRole[]): ModulePermissions {
  const merged: ModulePermissions = {};

  for (const module of MODULES) {
    merged[module] = {};
    for (const action of ACTIONS) {
      // Check if any role grants this permission
      const hasPermission = roles.some(role => {
        const rolePerms = ROLE_PERMISSIONS[role];
        const modulePerms = rolePerms?.[module];
        return modulePerms?.[action] === true;
      });
      if (hasPermission) {
        merged[module]![action] = true;
      }
    }
  }

  return merged;
}

/**
 * Check if a set of roles has a specific permission.
 */
export function hasPermission(roles: AppRole[], module: Module, action: Action): boolean {
  return roles.some(role => {
    const rolePerms = ROLE_PERMISSIONS[role];
    const modulePerms = rolePerms?.[module];
    return modulePerms?.[action] === true;
  });
}

/**
 * Check if a set of roles can access a module (has at least view permission).
 */
export function canAccessModule(roles: AppRole[], module: Module): boolean {
  return hasPermission(roles, module, 'view');
}

/**
 * Get a human-readable summary of what a role can do.
 */
export function getRoleCapabilitySummary(role: AppRole): string[] {
  const capabilities: string[] = [];
  const perms = ROLE_PERMISSIONS[role];

  for (const module of MODULES) {
    const modulePerms = perms[module];
    if (!modulePerms || Object.keys(modulePerms).length === 0) continue;

    const moduleInfo = MODULE_INFO[module];
    const actions: string[] = [];

    if (modulePerms.manage) {
      actions.push('full access');
    } else {
      if (modulePerms.view) actions.push('view');
      if (modulePerms.add) actions.push('add');
      if (modulePerms.edit) actions.push('edit');
      if (modulePerms.delete) actions.push('delete');
      if (modulePerms.export) actions.push('export');
    }

    if (actions.length > 0) {
      capabilities.push(`${moduleInfo.label}: ${actions.join(', ')}`);
    }
  }

  return capabilities;
}
